import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../assets/css/ResetPassword.css";
import axiosInstance from "../components/api/axiosInstance";

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: ""
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null); // null: checking, true: valid, false: invalid

  useEffect(() => {
    // Validate token on mount
    if (!token) {
      setTokenValid(false);
      setError("Invalid or missing reset token. Please request a new password reset link.");
    } else {
      setTokenValid(true);
    }
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user starts typing
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation
    if (!formData.newPassword || !formData.confirmPassword) {
      setError("All fields are required");
      return;
    }

    if (formData.newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!token) {
      setError("Invalid reset token. Please request a new password reset link.");
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post('/reset-password', {
        reset_token: token,
        new_password: formData.newPassword
      });

      if (response.data && response.data.status === "success") {
        const successMessage = response.data.message || "Password reset successfully! Redirecting to login...";
        setSuccess(successMessage);
        setFormData({
          newPassword: "",
          confirmPassword: ""
        });
        
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        const errorMsg = response.data?.message || "Failed to reset password. Please try again.";
        setError(errorMsg);
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || (error.request ? "Network error." : "Reset password failed.");
      console.error("Reset password error:", msg);
      
      if (error.response && error.response.data) {
        const errorMsg = error.response.data.message || 
                        error.response.data.error || 
                        `Server error: ${error.response.status}`;
        setError(errorMsg);
      } else if (error.request) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("Failed to reset password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === false) {
    return (
      <div className="resetpassword-container">
        <div className="resetpassword-form">
          <h2>Invalid Reset Link</h2>
          <div className="error-content">
            <p className="error-message">{error || "Invalid or expired reset token."}</p>
            <p className="error-description">
              This password reset link is invalid or has expired. Please request a new password reset link.
            </p>
          </div>
          <button 
            type="button" 
            className="submit-button" 
            onClick={() => navigate("/forgot-password")}
          >
            Request New Reset Link
          </button>
          <button 
            type="button" 
            className="back-link" 
            onClick={() => navigate("/")}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="resetpassword-container">
      <form className="resetpassword-form" onSubmit={handleSubmit}>
        <h2>Reset Password</h2>
        <p className="resetpassword-description">
          Enter your new password below.
        </p>

        {error && <p className="error">{error}</p>}
        {success && <p className="success">{success}</p>}

        <div className="input-group">
          <label>New Password</label>
          <input
            type="password"
            name="newPassword"
            placeholder=""
            value={formData.newPassword}
            onChange={handleInputChange}
            required
            disabled={loading}
          />
          <span className="hint-text">Must be at least 6 characters</span>
        </div>

        <div className="input-group">
          <label>Confirm New Password</label>
          <input
            type="password"
            name="confirmPassword"
            placeholder=""
            value={formData.confirmPassword}
            onChange={handleInputChange}
            required
            disabled={loading}
          />
        </div>

        <button type="submit" className="submit-button" disabled={loading || tokenValid === null}>
          {loading ? "Resetting..." : "Reset Password"}
        </button>

        <div className="back-to-login">
          <button 
            type="button" 
            className="back-link" 
            onClick={() => navigate("/")}
            disabled={loading}
          >
            Back to Login
          </button>
        </div>
      </form>
    </div>
  );
}

export default ResetPassword;
